import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import {
  FiX,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUsers,
  FiVideo,
  FiCheck,
  FiXCircle,
  FiHelpCircle,
  FiTrash2,
  FiCopy,
  FiExternalLink,
} from "react-icons/fi";
import {
  formatDate,
  formatTime,
  formatDuration,
  getRSVPLabel,
  canJoinMeeting,
} from "../../../../utils/calendarUtils";
import { UserContext } from "../../../../context/UserContext";
import { useRoomCalendar } from "../../../../context/RoomCalendarContext";
import "./CalendarEventModal.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: { opacity: 0, scale: 0.9, y: 20, transition: { duration: 0.2 } },
};

const CalendarEventModal = ({
  event,
  roomId,
  userRole,
  onClose,
  onUpdate,
  eventTypes,
}) => {
  const { user } = useContext(UserContext);
  const { deleteEvent, updateRSVP } = useRoomCalendar();
  const [loading, setLoading] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const currentUserId = user?.id || user?._id;
  const isCreator = event.createdBy?._id === currentUserId;
  const canCancel = isCreator || userRole?.isLeader;

  // Calculate duration
  const duration =
    new Date(event.endDate) - new Date(event.startDate) || event.duration;

  // Count RSVPs
  const acceptedCount = event.acceptedCount || 0;
  const declinedCount = event.declinedCount || 0;
  const tentativeCount =
    event.participants?.filter((p) => p.rsvp === "tentative").length || 0;
  const pendingCount =
    event.participants?.filter((p) => p.rsvp === "pending").length || 0;

  // Current user's RSVP
  const myParticipation = event.participants?.find(
    (p) => p.user._id === currentUserId || p.user === currentUserId
  );
  const myRSVP = myParticipation?.rsvp || "pending";

  // Get event config
  const eventConfig = eventTypes?.[event.eventType] ||
    eventTypes?.other || { color: "#64748b", bg: "#f1f5f9" };

  // Join status
  const joinStatus = canJoinMeeting(event);

  const handleCancelEvent = async () => {
    if (!window.confirm("Are you sure you want to cancel this event?")) {
      return;
    }

    try {
      setLoading(true);
      await deleteEvent(roomId, event._id);
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) { /* ignored */ } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (rsvp) => {
    if (rsvpLoading || myRSVP === rsvp) return;

    try {
      setRsvpLoading(true);
      await updateRSVP(roomId, event._id, rsvp);
    } catch (error) { /* ignored */ } finally {
      setRsvpLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const handleJoinMeeting = () => {
    if (event.meetingLinks?.zoom?.url) {
      window.open(event.meetingLinks.zoom.url, "_blank");
    } else if (event.meetingLinks?.googleMeet?.url) {
      window.open(event.meetingLinks.googleMeet.url, "_blank");
    } else if (event.meetingLinks?.teams?.url) {
      window.open(event.meetingLinks.teams.url, "_blank");
    }
  };

  return (
    <motion.div
      className="rcevm-overlay"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="rcevm-container"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="rcevm-header"
          style={{ "--event-color": eventConfig.color }}
        >
          <div className="rcevm-header-content">
            <span
              className="rcevm-type-badge"
              style={{ background: eventConfig.bg, color: eventConfig.color }}
            >
              {event.eventType}
            </span>
            <h2 className="rcevm-title">{event.title}</h2>
            {event.status && (
              <span className={`rcevm-status rcevm-status--${event.status}`}>
                {event.status}
              </span>
            )}
          </div>
          <motion.button
            className="rcevm-close-btn"
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiX />
          </motion.button>
        </div>

        {/* Content */}
        <div className="rcevm-content">
          {/* Description */}
          {event.description && (
            <div className="rcevm-section">
              <p className="rcevm-description">{event.description}</p>
            </div>
          )}

          {/* Date & Time */}
          <div className="rcevm-section">
            <h4 className="rcevm-section-title">
              <FiCalendar />
              Date & Time
            </h4>
            <div className="rcevm-info-grid">
              <div className="rcevm-info-item">
                <span className="rcevm-info-label">Date</span>
                <span className="rcevm-info-value">
                  {formatDate(event.startDate)}
                </span>
              </div>
              <div className="rcevm-info-item">
                <span className="rcevm-info-label">Time</span>
                <span className="rcevm-info-value">
                  {formatTime(event.startDate)} - {formatTime(event.endDate)}
                </span>
              </div>
              <div className="rcevm-info-item">
                <span className="rcevm-info-label">Duration</span>
                <span className="rcevm-info-value">
                  {formatDuration(duration)}
                </span>
              </div>
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className="rcevm-section">
              <h4 className="rcevm-section-title">
                <FiMapPin />
                Location
              </h4>
              <p className="rcevm-location">{event.location}</p>
            </div>
          )}

          {/* Meeting Links */}
          {event.meetingLinks && Object.keys(event.meetingLinks).length > 0 && (
            <div className="rcevm-section">
              <h4 className="rcevm-section-title">
                <FiVideo />
                Join Meeting
              </h4>

              <div className="rcevm-meeting-links">
                {event.meetingLinks.zoom && (
                  <div className="rcevm-meeting-card">
                    <div className="rcevm-meeting-header">
                      <span className="rcevm-meeting-platform">Zoom</span>
                      <a
                        href={event.meetingLinks.zoom.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rcevm-meeting-link"
                      >
                        <FiExternalLink />
                        Open
                      </a>
                    </div>
                    {event.meetingLinks.zoom.meetingId && (
                      <div className="rcevm-meeting-detail">
                        <span>Meeting ID:</span>
                        <strong>{event.meetingLinks.zoom.meetingId}</strong>
                        <motion.button
                          className="rcevm-copy-btn"
                          onClick={() =>
                            copyToClipboard(event.meetingLinks.zoom.meetingId)
                          }
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FiCopy />
                        </motion.button>
                      </div>
                    )}
                    {event.meetingLinks.zoom.password && (
                      <div className="rcevm-meeting-detail">
                        <span>Password:</span>
                        <strong>{event.meetingLinks.zoom.password}</strong>
                        <motion.button
                          className="rcevm-copy-btn"
                          onClick={() =>
                            copyToClipboard(event.meetingLinks.zoom.password)
                          }
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FiCopy />
                        </motion.button>
                      </div>
                    )}
                  </div>
                )}

                {event.meetingLinks.googleMeet && (
                  <a
                    href={event.meetingLinks.googleMeet.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rcevm-meeting-btn"
                  >
                    <FiVideo />
                    Join Google Meet
                    <FiExternalLink />
                  </a>
                )}

                {event.meetingLinks.teams && (
                  <a
                    href={event.meetingLinks.teams.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rcevm-meeting-btn"
                  >
                    <FiVideo />
                    Join Microsoft Teams
                    <FiExternalLink />
                  </a>
                )}

                {/* Quick Join Button */}
                <motion.button
                  className={`rcevm-join-btn ${
                    joinStatus.canJoin ? "rcevm-join-btn--active" : ""
                  }`}
                  onClick={handleJoinMeeting}
                  disabled={!joinStatus.canJoin}
                  whileHover={joinStatus.canJoin ? { scale: 1.02 } : {}}
                  whileTap={joinStatus.canJoin ? { scale: 0.98 } : {}}
                  title={joinStatus.message}
                >
                  <FiVideo />
                  {joinStatus.canJoin ? "Join Meeting Now" : joinStatus.message}
                </motion.button>
              </div>
            </div>
          )}

          {/* Participants */}
          <div className="rcevm-section">
            <h4 className="rcevm-section-title">
              <FiUsers />
              Participants ({event.participants?.length || 0})
            </h4>

            <div className="rcevm-rsvp-summary">
              <div className="rcevm-rsvp-stat rcevm-rsvp-stat--accepted">
                <FiCheck />
                <span>{acceptedCount} Going</span>
              </div>
              <div className="rcevm-rsvp-stat rcevm-rsvp-stat--declined">
                <FiXCircle />
                <span>{declinedCount} Not Going</span>
              </div>
              <div className="rcevm-rsvp-stat rcevm-rsvp-stat--tentative">
                <FiHelpCircle />
                <span>{tentativeCount} Maybe</span>
              </div>
              <div className="rcevm-rsvp-stat rcevm-rsvp-stat--pending">
                <FiClock />
                <span>{pendingCount} Pending</span>
              </div>
            </div>

            <div className="rcevm-participants-list">
              {event.participants?.map((participant) => (
                <div key={participant.user._id} className="rcevm-participant">
                  {participant.user.profilePhotoUrl ? (
                    <img
                      src={participant.user.profilePhotoUrl}
                      alt={participant.user.name}
                      className="rcevm-participant-avatar"
                    />
                  ) : (
                    <div className="rcevm-participant-avatar-placeholder">
                      {participant.user.name?.charAt(0) || "?"}
                    </div>
                  )}
                  <div className="rcevm-participant-info">
                    <span className="rcevm-participant-name">
                      {participant.user.name}
                    </span>
                    {participant.isOrganizer && (
                      <span className="rcevm-organizer-badge">Organizer</span>
                    )}
                  </div>
                  <span
                    className={`rcevm-participant-rsvp rcevm-rsvp--${participant.rsvp}`}
                  >
                    {getRSVPLabel(participant.rsvp)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Your Response */}
          <div className="rcevm-section">
            <h4 className="rcevm-section-title">Your Response</h4>
            <div className="rcevm-rsvp-buttons">
              <motion.button
                className={`rcevm-rsvp-btn rcevm-rsvp-btn--accepted ${
                  myRSVP === "accepted" ? "active" : ""
                }`}
                onClick={() => handleRSVP("accepted")}
                disabled={rsvpLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiCheck />
                Going
              </motion.button>
              <motion.button
                className={`rcevm-rsvp-btn rcevm-rsvp-btn--declined ${
                  myRSVP === "declined" ? "active" : ""
                }`}
                onClick={() => handleRSVP("declined")}
                disabled={rsvpLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiXCircle />
                Not Going
              </motion.button>
              <motion.button
                className={`rcevm-rsvp-btn rcevm-rsvp-btn--tentative ${
                  myRSVP === "tentative" ? "active" : ""
                }`}
                onClick={() => handleRSVP("tentative")}
                disabled={rsvpLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiHelpCircle />
                Maybe
              </motion.button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        {canCancel && (
          <div className="rcevm-footer">
            <motion.button
              className="rcevm-cancel-event-btn"
              onClick={handleCancelEvent}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiTrash2 />
              {loading ? "Cancelling..." : "Cancel Event"}
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CalendarEventModal;
